/**
 * T155P4 — RuleUndoBanner
 *
 * Compact banner shown after a successful rule commit.
 * Provides a time-limited undo window via the undo/:batchId endpoint.
 *
 * data-testid="rule-undo-banner"
 * data-testid="rule-undo-btn"
 */
import { useEffect, useState } from 'react';
import { Undo2, CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAppDispatch, useAppSelector } from '@/features/hooks';
import { undoRuleBatch, clearLastBatchId } from '@/features/transactionRules/transactionRulesSlice';
import { useToast } from '@/hooks/use-toast';

/** Auto-dismiss after this many ms (30 s) */
const UNDO_WINDOW_MS = 30_000;

interface RuleUndoBannerProps {
  /** Called after a successful undo so the parent can refresh the queue */
  onUndone: () => void;
}

export function RuleUndoBanner({ onUndone }: RuleUndoBannerProps) {
  const dispatch = useAppDispatch();
  const { toast } = useToast();

  const batchId = useAppSelector((s) => s.transactionRules.lastCommitBatchId);
  const isUndoing = useAppSelector((s) => s.transactionRules.isUndoing);

  const [secondsLeft, setSecondsLeft] = useState(UNDO_WINDOW_MS / 1000);

  // Countdown + auto-dismiss
  useEffect(() => {
    if (!batchId) return;
    setSecondsLeft(UNDO_WINDOW_MS / 1000);

    const interval = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          clearInterval(interval);
          dispatch(clearLastBatchId());
          return 0;
        }
        return s - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [batchId, dispatch]);

  if (!batchId) return null;

  const handleUndo = async () => {
    try {
      const result = await dispatch(undoRuleBatch(batchId)).unwrap();
      toast({
        title: `Reverted ${result.revertedCount} transaction${result.revertedCount !== 1 ? 's' : ''}`,
        description: result.status === 'partial'
          ? `${result.notFoundCount + result.failedCount} could not be reverted.`
          : 'All changes have been rolled back.',
      });
      onUndone();
    } catch {
      toast({
        title: 'Undo failed',
        description: 'Could not revert the automation batch. Please try again.',
        variant: 'destructive',
      });
    }
  };

  return (
    <div
      className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 rounded-lg border border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950/30"
      data-testid="rule-undo-banner"
    >
      <div className="flex items-center gap-2 min-w-0">
        <CheckCircle2 className="size-4 text-green-600 dark:text-green-400 shrink-0" />
        <p className="text-sm font-medium text-green-900 dark:text-green-200">
          Rules applied successfully
        </p>
        <span className="text-xs text-green-600 dark:text-green-400 tabular-nums">
          · Undo available for {secondsLeft}s
        </span>
      </div>
      <Button
        size="sm"
        variant="outline"
        className="min-h-[36px] border-green-300 dark:border-green-700 text-green-700 dark:text-green-300 hover:bg-green-100 dark:hover:bg-green-900/40 shrink-0"
        onClick={handleUndo}
        disabled={isUndoing}
        data-testid="rule-undo-btn"
      >
        {isUndoing ? (
          <>
            <Loader2 className="size-3.5 mr-1.5 animate-spin" />
            Undoing…
          </>
        ) : (
          <>
            <Undo2 className="size-3.5 mr-1.5" />
            Undo
          </>
        )}
      </Button>
    </div>
  );
}
